// import axios from "axios";

// const api = axios.create({
//   baseURL: process.env.REACT_APP_API_URL,
// });

// // Interceptor to handle 401 and refresh access token
// api.interceptors.response.use(
//   (response) => response,
//   async (error) => {
//     const originalRequest = error.config;

//     // 🔴 Handle inactive user / enterprise → logout
//     if (error.response?.status === 403) {
//       localStorage.clear();
//       sessionStorage.clear();
//       window.location.href = "/";
//       return Promise.reject(error);
//     }

//     if (error.response?.status === 401 && !originalRequest._retry) {
//       originalRequest._retry = true;

//       const refreshToken = localStorage.getItem("refresh_token");
//       if (!refreshToken) {
//         window.location.href = "/"; // redirect to login if no refresh token
//         return Promise.reject(error);
//       }

//       try {
//         const res = await axios.post(
//           `${process.env.REACT_APP_API_URL}/auth/refresh`,
//           {
//             refresh_token: refreshToken,
//           }
//         );

//         const newAccessToken = res.data.access_token;

//         // Update storage
//         localStorage.setItem("access_token", newAccessToken);
//         sessionStorage.setItem("access_token", newAccessToken);

//         // Update original request
//         originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;

//         return api(originalRequest);
//       } catch (err) {
//         // Refresh token invalid → redirect to login
//         localStorage.clear();
//         sessionStorage.clear();
//         window.location.href = "/";
//         return Promise.reject(err);
//       }
//     }

//     return Promise.reject(error);
//   }
// );

// export default api;

import axios from "axios";
import { store } from "./redux/store";
import { startLoading, stopLoading } from "./redux/loadingSlice";
import { UpdateManager } from "./services/UpdateManager";

// ================================
// 🔁 SINGLE-FLIGHT TOKEN REFRESH
// Prevents multiple parallel /auth/refresh calls when many requests 401 at once.
// ================================
let refreshInFlight = null;

// ================================
// 🚀 CREATE AXIOS INSTANCE
// ================================
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
  // timeout: 2000,
});

// ================================
// 🔧 UTILITY: SKIP LOADER FOR CHAT
// ================================
const shouldSkipLoader = (url = "") => {
  return (
    url.includes("/chat/sessions") ||
    url.includes("/chat/session/") ||
    url.includes("/chat/session1/") ||
    url.includes("/chat/query") ||
    url.includes("/chat/feedback") ||
    url.includes("/chat/save") ||
    url.includes("/chat/chatroute") ||
    url.includes("/chat/new-session") ||
    url.includes("/chat/job") ||
    url.includes("/chat/result") ||
    url.includes("/chat/update-response-time") ||
    url.includes("/chat/realtime-stt") ||
    url.includes("/chat/stop/") ||
    url.includes("/chat/stop-stream") ||
    url.includes("/enterprise-chat-security-settings") ||
    url.includes("/uploaded-files/newCheckDuplicates") ||
    url.includes("/uploaded-files/newUpload") ||
    url.includes("/uploaded-files/saveDuplicateFiles") ||
    url.includes("/uploaded-files/newUpload_webpage") ||
    url.includes("/user-profile/preferences") ||
    url.includes("/uploaded-files/newUpload_youtube") ||
    url.includes("/uploaded-files/newQueryUpload") ||
    url.includes("/trained-documents/get-trained-docs") ||
    url.includes("/uploaded-files/global-user-docs") ||
    url.includes("/trained-documents/move-items") ||
    url.includes("/trained-documents/move-uploaded-files") ||
    url.includes("/trained-documents/create-folder") ||
    url.includes("/ingestion/status/") ||
    url.includes("/context/files") ||
    url.includes("/context/upload") ||
    url.includes("/context/")||
    url.includes("/projects") ||
    url.includes("/repo-organizer") ||
    url.includes("/enterprise-admin-setting/approved") 
  );
};

// ================================
// 🔐 UTILITY: AUTH API CHECK
// ================================
const isAuthApi = (url = "") => {
  return (
    url.includes("/auth/login") ||
    url.includes("/auth/refresh") ||
    url.includes("/auth/captcha") ||
    url.includes("/auth/register-request") ||
    url.includes("/auth/finish-registration")
  );
};

const isMissingOrInvalidBearer = (value) => {
  if (!value) return true;
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  return (
    trimmed === "" ||
    trimmed === "Bearer" ||
    trimmed === "Bearer null" ||
    trimmed === "Bearer undefined"
  );
};

// ================================
// 🔁 GLOBAL REQUEST INTERCEPTOR
// ================================
api.interceptors.request.use(
  (config) => {
    // Attach latest access token by default (avoids stale tokens captured by callers)
    const accessToken =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");
    const url = config?.url || "";
    if (accessToken && !isAuthApi(url) && !config?.skipAuth) {
      config.headers = config.headers || {};
      // Always overwrite Authorization to prevent stale tokens from callers.
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // `silent` is used across the app to suppress global UX effects (toasts, redirects, etc).
    // Treat it as loader-skip too so background polling doesn't interrupt ChatPage.
    config.__skipLoader =
      Boolean(config?.skipLoader) ||
      Boolean(config?.silent) ||
      shouldSkipLoader(config.url);

    if (!config.__skipLoader) {
      store.dispatch(startLoading());
      config.__startedLoading = true;
    }
    return config;
  },
  (error) => {
    store.dispatch(stopLoading());
    return Promise.reject(error);
  },
);

// ================================
// 🔁 GLOBAL RESPONSE INTERCEPTOR
// ================================
api.interceptors.response.use(
  (response) => {
    if (response?.config?.__startedLoading) {
      store.dispatch(stopLoading());
    }

    // Reuse the existing user preferences traffic to hydrate UpdateManager's
    // backend-owned release notes acknowledgement state (no extra API calls).
    try {
      const url = String(response?.config?.url || "");
      if (url.includes("/user-profile/preferences") && response?.data) {
        UpdateManager.onUserPreferencesResponse(response.data);
      }
    } catch {}

    return response;
  },
  async (error) => {
    if (error?.config?.__startedLoading) {
      store.dispatch(stopLoading());
    }

    const originalRequest = error.config;
    const url = originalRequest?.url || "";

    // ================================
    // 🌐 NETWORK ERROR
    // ================================
    if (!error.response) {
      console.error("🌐 Network error! No server response.");
      return Promise.reject(error);
    }

    // ================================
    // ❌ 403 → FORCE LOGOUT (NOT LOGIN)
    // ================================
    if (error.response.status === 403 && !isAuthApi(url)) {
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = "/";
      return Promise.reject(error);
    }

    // ================================
    // 🔁 401 → ACCESS TOKEN EXPIRED
    // (NOT FOR LOGIN / CAPTCHA)
    // ================================
    if (
      error.response.status === 401 &&
      !originalRequest._retry &&
      !isAuthApi(url)
    ) {
      originalRequest._retry = true;

      const refreshToken =
        localStorage.getItem("refresh_token") ||
        sessionStorage.getItem("refresh_token");
      if (!refreshToken) {
        window.location.href = "/";
        return Promise.reject(error);
      }

      try {
        if (!refreshInFlight) {
          refreshInFlight = axios
            .post(`${process.env.REACT_APP_API_URL}/auth/refresh`, {
              refresh_token: refreshToken,
            })
            .then((res) => {
              const newAccessToken = res.data?.access_token;
              const newRefreshToken = res.data?.refresh_token;

              if (newAccessToken) {
                localStorage.setItem("access_token", newAccessToken);
                sessionStorage.setItem("access_token", newAccessToken);
              }
              if (newRefreshToken) {
                localStorage.setItem("refresh_token", newRefreshToken);
                sessionStorage.setItem("refresh_token", newRefreshToken);
              }

              return newAccessToken;
            })
            .finally(() => {
              refreshInFlight = null;
            });
        }

        const newAccessToken = await refreshInFlight;
        if (!newAccessToken) {
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = "/";
          return Promise.reject(error);
        }

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        return api(originalRequest); // 🔁 Retry original API
      } catch (err) {
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/";
        return Promise.reject(err);
      }
    }

    // ❗ Login / captcha / validation errors
    // handled in component (NO redirect)
    return Promise.reject(error);
  },
);

export default api;
